import * as z from "zod";

/**
 * Runtime contract of the desktop shell (quick entry, tray, menu, screenshot, deep links).
 * Plain JS like local-settings.js: the Electron main process loads it from node_modules,
 * where TypeScript sources cannot run. Types live in desktop-shell.d.ts.
 */

/** URL scheme of desktop deep links, without the colon. */
export const DESKTOP_DEEP_LINK_PROTOCOL = "rakazo";

/** Ids travel into app navigation only, never into a URL the desktop loads. */
export const DesktopEntityId = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

/** Validated target of a deep link: a bot or a conversation by id. */
export const DesktopDeepLinkTarget = z
  .object({ kind: z.enum(["bot", "conversation"]), id: DesktopEntityId })
  .strict();

/** Commands the native menu forwards to the web app. */
export const DesktopMenuCommand = z.enum(["newChat", "search", "toggleSidebar", "settings"]);

/** A count only, so the tray never carries message content. */
export const DesktopTrayBadge = z.number().int().min(0).max(9999);

/** Payload sent each time the quick entry window is shown again. */
export const DesktopQuickEntryShow = z.object({ botId: DesktopEntityId.nullable() }).strict();

/** PNG capture of the screen under the requesting Rakazo window. */
export const DesktopScreenshot = z
  .object({
    pngBase64: z.string().min(1),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  })
  .strict();

/** Global quick-entry shortcuts the person can pick; `null` turns the shortcut off. */
export const DESKTOP_QUICK_ENTRY_SHORTCUTS = [
  "Alt+Space",
  "CommandOrControl+Shift+Space",
  "CommandOrControl+Alt+Space",
];

/** Desktop settings stored in userData: quick entry shortcut and close to tray. */
export const DesktopPreferences = z
  .object({
    quickEntryShortcut: z.enum(DESKTOP_QUICK_ENTRY_SHORTCUTS).nullable(),
    closeToTray: z.boolean(),
  })
  .strict();

/** Preferences used when the file is missing or invalid. */
export const DEFAULT_DESKTOP_PREFERENCES = Object.freeze({
  quickEntryShortcut: "Alt+Space",
  closeToTray: true,
});

/**
 * Accepts exactly `rakazo://bot/<id>` and `rakazo://conversation/<id>` (one optional trailing
 * slash, which Windows sometimes appends). Everything else yields null and is dropped.
 * @param {unknown} raw
 */
export function parseDesktopDeepLink(raw) {
  if (typeof raw !== "string" || raw.length > 512) return null;
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== `${DESKTOP_DEEP_LINK_PROTOCOL}:`) return null;
  if (url.username || url.password || url.port || url.search || url.hash) return null;
  const segments = url.pathname.split("/").slice(1);
  if (segments.length === 2 && segments[1] === "") segments.pop();
  if (segments.length !== 1) return null;
  const parsed = DesktopDeepLinkTarget.safeParse({ kind: url.hostname, id: segments[0] });
  return parsed.success ? parsed.data : null;
}

/**
 * Builds the canonical rakazo:// URL for a target.
 * @param {{ kind: "bot" | "conversation", id: string }} target
 */
export function desktopDeepLinkUrl(target) {
  return `${DESKTOP_DEEP_LINK_PROTOCOL}://${target.kind}/${target.id}`;
}
